import { useEffect, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { planShowRideDays, fetchUndatedClassRides, fetchUnscheduledRides, mergeDedupedRides } from "./showRideDayPlan";

// Load every ride in the active show as a flat list, each annotated with its
// class name (#560). Powers the competitor score-sheet lookup: an admin finds a
// ride by horse / rider name or CNO without knowing the ride id. Per SPIKE #561
// this is a direct query + client-side filter — one show's rides are small
// enough to hold and filter in the browser, no search index.
//
// #861/#1906 — GET /ride's showId+day filter (#637) backs a per-day
// progressive load: the show's most recent day's rides arrive first so a
// caller can paint immediately, while the rest of the show's days load one at
// a time in the background and get merged in (`loadingMore` stays true until
// they're all in). Every caller (RideEditor, PeopleManager, ReportsPage) uses
// this same path now — see docs/score/query-performance.md's request-shape
// rule: a screen's data is one request (or one per day), never one per class.
//
// `error` is set (and rides left as whatever already loaded) when a fetch
// fails, so a caller can distinguish "the show has no rides" from "loading
// rides failed" instead of both rendering as an empty list.
//
// #3186 -- `loading` is DERIVED, not just flipped inside the effect. State
// carries `forShowId` (which show its rides belong to); until that matches the
// `showId` passed in, the hook returns a loading, empty result. So the very
// first render with a showId already reports `loading: true` (a `loading:
// false` first render let every caller's "no rides" text flash for a frame
// before the effect ran), and a switch from show A to show B never returns
// A's rides under B.
const EMPTY = { rides: [], loading: false, loadingMore: false, error: null };
const LOADING = { rides: [], loading: true, loadingMore: false, error: null };

export function useShowRides(showId) {
  const scoreApi = useScoreApi();
  const [state, setState] = useState({ ...EMPTY, forShowId: null });

  useEffect(() => {
    if (!showId) {
      setState({ ...EMPTY, forShowId: null });
      return undefined;
    }
    let cancelled = false;
    setState({ rides: [], loading: true, loadingMore: false, error: null, forShowId: showId });
    (async () => {
      try {
        // #1909 — the show's per-show summary row (dayKeys, ascending),
        // fetched alongside the class list rather than after it: one extra
        // GetItem, in parallel, not a second round trip. A show that hasn't
        // been re-reconciled since #1909 shipped (or any other GET failure)
        // resolves to null here and the day-key derivation below falls back
        // to reducing the class list, same as before this card.
        const [classes, showSummary] = await Promise.all([
          scoreApi.classes.listClasses(showId).then(unwrap),
          scoreApi.shows
            .getShow(showId)
            .then(unwrap)
            .then((s) => s?.summary ?? null)
            .catch(() => null),
        ]);
        const classNameById = Object.fromEntries(
          classes.map((c) => [c.id, c.className || c.classNumber || c.id]),
        );
        // #1966 (split from #1913 AC3, decided 2026-08-26) — entity-first
        // with the stamped name as fallback: the live `classes` join stays
        // primary (it's always current, fetched fresh above), and
        // `ride.className` (the reconcile-stamped copy) is read ONLY when
        // the join can't resolve a name for that ride's classId (e.g. the
        // class hasn't loaded, or was deleted/renumbered out from under an
        // already-fetched ride). Never the other way around: a
        // stamped-primary order would show a stale class name until the
        // next reconcile picks up a ride's move to a different class,
        // which is the regression #1966 exists to avoid.
        const withClassName = (list) =>
          (list ?? []).map((r) => ({ ...r, className: classNameById[r.classId] ?? r.className ?? null }));

        // Most-recent-day-first order. Prefer the summary's `dayKeys`
        // (written by reconcile, ascending) over reducing the class list
        // ourselves; GET /ride's showId+day filter (#637) backs each dated
        // call either way: one request per day, not one per class. A class
        // with NO scheduledTime (classDayKey null — an emergency/manual-add
        // class, #386) can't be reached by any day filter, so its rides are
        // fetched directly by classId and folded into the first (immediate)
        // paint alongside the most recent day — never dropped, and never
        // held back behind the background loop.
        //
        // #1963 — the day/undated-class plan itself is shared with
        // useNextRides (showRideDayPlan.js), which needs the identical
        // dedup but awaits every day up front instead of painting
        // progressively.
        //
        // #2040 — a ride with no scheduledTime at ALL inside an otherwise
        // dated class matches no day filter and isn't in undatedClassIds
        // either (that only covers classes whose EVERY ride is timeless).
        // fetchUnscheduledRides closes that gap with one more show-wide call
        // (not per class), merged in here with mergeDedupedRides.
        const { dayKeys, undatedClassIds } = planShowRideDays(classes, showSummary);
        const fetchUndated = () => fetchUndatedClassRides(scoreApi, undatedClassIds);
        // #2040 — the show-wide "no scheduledTime at all" carve-out (one call,
        // not per class): catches a timeless ride sitting inside an otherwise
        // dated class, which neither a day call nor undatedClassIds reaches.
        // Fetched alongside the first paint so it's never a second round trip
        // behind the background day loop.
        const fetchUnscheduled = () => fetchUnscheduledRides(scoreApi, showId);

        if (dayKeys.length === 0) {
          // No dated classes at all (e.g. an all-manual or empty show) — a
          // single show-wide call already covers everything, including any
          // timeless rides (there's no day filter for #2040 to bypass here).
          const list = unwrap(await scoreApi.rides.listRides(undefined, showId));
          if (!cancelled) setState({ rides: withClassName(list), loading: false, loadingMore: false, error: null, forShowId: showId });
          return;
        }

        const [firstDay, ...restDays] = dayKeys;
        const [firstDayList, undatedList, unscheduledList] = await Promise.all([
          scoreApi.rides.listRides(undefined, showId, firstDay).then(unwrap),
          fetchUndated(),
          fetchUnscheduled(),
        ]);
        if (cancelled) return;
        setState({
          rides: withClassName(mergeDedupedRides(firstDayList, undatedList, unscheduledList)),
          loading: false,
          loadingMore: restDays.length > 0,
          error: null,
          forShowId: showId,
        });

        for (const day of restDays) {
          if (cancelled) return;
          try {
            const list = unwrap(await scoreApi.rides.listRides(undefined, showId, day));
            if (!cancelled) {
              setState((s) => ({ ...s, rides: [...s.rides, ...withClassName(list)] }));
            }
          } catch (err) {
            // One bad day shouldn't wipe out the days already rendered —
            // surface it (the list so far stays visible) and stop trying to
            // fill in the rest.
            if (!cancelled) setState((s) => ({ ...s, loadingMore: false, error: err }));
            return;
          }
        }
        if (!cancelled) setState((s) => ({ ...s, loadingMore: false }));
      } catch (err) {
        if (!cancelled) setState({ rides: [], loading: false, loadingMore: false, error: err, forShowId: showId });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showId, scoreApi]);

  if (!showId) return EMPTY;
  return state.forShowId === showId ? state : LOADING;
}

export default useShowRides;
